import * as React from "react";
import {
  SearchHistory,
  FormattedCard,
  Card,
  LastViewedDate,
  DefaultCard,
  ActivityProductInfo,
  ActivityDetailProductInfo,
  FlightProductInfo,
  PackageProductInfo,
  PropertyProductInfo,
  PropertyDetailProductInfo,
  FlightDetailProductInfo,
  RecentlyViewedWrapperFlexComponent,
} from "./typings";
import { useLocalization } from "@shared-ui/localization-context";
import { withStores } from "src/stores";
import { locKeys } from "./l10n";
import { UitkHeading, UitkHeadingProps } from "uitk-react-text";
import { RecentlyViewedCards } from "./RecentlyViewedCards";
import { FlexComponentProps } from "src/typings/flexFramework/FlexComponent";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";
import { RecentlyViewedStore } from "src/stores/RecentlyViewedStore";

export enum RouteType {
  ROUND_TRIP = "ROUND_TRIP",
  ONE_WAY = "ONE_WAY",
  MULTICITY = "MULTICITY",
}

export interface RecentlyViewedWrapperProps extends FlexComponentProps {
  flexComponent: RecentlyViewedWrapperFlexComponent;
  context: ExtendedContextStore;
  recentlyViewed: RecentlyViewedStore;
}

export const RecentlyViewedWrapper = withStores("recentlyViewed")((props: RecentlyViewedWrapperProps) => {
  const { recentlyViewed, context, flexComponent } = props;
  const { formatText } = useLocalization();
  const flexModel = flexComponent.model;
  const MAX_CARD_COUNT = 3;
  const minAmountOfCardsToDisplay = 2;
  const headingConfig: UitkHeadingProps = { level: 2, size: 700 };
  const routeTypeMap = {
    [RouteType.ROUND_TRIP]: () => formatText(locKeys.flightRoundTripText),
    [RouteType.ONE_WAY]: () => formatText(locKeys.flightOneWayText),
    [RouteType.MULTICITY]: () => formatText(locKeys.flightMultiCityText),
  };

  const ItemTypeMap = {
    ACTIVITY_SEARCH: { icon: "lob_activities", type: "ACTIVITY_SEARCH" },
    ACTIVITY_DETAIL: { icon: "lob_activities", type: "ACTIVITY_DETAIL" },
    FLIGHT_SEARCH: { icon: "lob_flights", type: "FLIGHT_SEARCH" },
    FLIGHT_DETAIL: { icon: "lob_flights", type: "FLIGHT_DETAIL" },
    PACKAGE_SEARCH: { icon: "lob_packages", type: "PACKAGE_SEARCH" },
    PROPERTY_SEARCH: { icon: "lob_hotels", type: "PROPERTY_SEARCH" },
    PROPERTY_DETAIL: { icon: "lob_hotels", type: "PROPERTY_DETAIL" },
  };
  const activeSearchesLobs = {
    ACTIVITY_SEARCH: flexModel.showRecentlySearchedLX,
    ACTIVITY_DETAIL: flexModel.showRecentlySearchedLX,
    FLIGHT_SEARCH: flexModel.showRecentlySearchedFlights,
    FLIGHT_DETAIL: flexModel.showRecentlySearchedFlights,
    PACKAGE_SEARCH: flexModel.showRecentlySearchedPackages,
    PROPERTY_SEARCH: flexModel.showRecentlySearchedHotels,
    PROPERTY_DETAIL: flexModel.showRecentlySearchedHotels,
  };
  const [recentlyViewedData, setRecentlyViewedData] = React.useState<DefaultCard[]>([]);

  React.useEffect(() => {
    recentlyViewed.fetchRecentlyViewed(context, MAX_CARD_COUNT, activeSearchesLobs).then((result) => {
      const flattenCardsData = result ? flattenAndSortSearchItems(result as SearchHistory[]) : [];
      const cardsData = formatArrayOfCards(flattenCardsData).filter((data) => data !== null);
      setRecentlyViewedData(cardsData);
    });
  }, []);

  /**
   * Context: Bex api returns an array of arrays grouping each type of search history
   * Transforms an array of array into one single array.
   * @param {SearchHistoy[]} cardsData - Array returned by the bex api call.
   * @returns {FormattedCard[]} - Formatted and sorted array by recent date.
   */

  const flattenAndSortSearchItems = (cardsData: SearchHistory[]): FormattedCard[] =>
    // @ts-ignore
    cardsData
      .map((results: SearchHistory) =>
        results.items?.map((item: Card) => ({
          ...item,
          type: results.type,
          lastViewedDate: formatDate(item.lastViewedDate),
        }))
      )
      .flat()
      .sort((a, b) => +b!.lastViewedDate - +a!.lastViewedDate);

  /**
   * Context: Search history types have different properties
   * Creates a new array based on the passed data by normalizing and setting a unique structure for each item.
   * @param {FormattedCard[]} cardsData - Formatted and sorted array by recent date.
   * @returns {DefaultCard[]} - Array with items that have the same properties
   */

  const formatArrayOfCards = (arrayOfCards: FormattedCard[]): DefaultCard[] =>
    // @ts-ignore
    arrayOfCards.map((result: FormattedCard) => getFormattedHistorySearch(result)).flat();

  const getFormattedHistorySearch = (historySearch: FormattedCard) => {
    switch (historySearch.type) {
      case ItemTypeMap.ACTIVITY_SEARCH.type:
        const activityProductInfo = historySearch.productInfo as ActivityProductInfo;

        return {
          id: historySearch.id,
          title: activityProductInfo.regionDetail?.name,
          line1: historySearch.localizedDateRange,
          line2: "",
          deepLinkUrl: activityProductInfo.searchUrl,
          icon: ItemTypeMap.ACTIVITY_SEARCH.icon,
        };

      case ItemTypeMap.ACTIVITY_DETAIL.type:
        const activityDetailProductInfo = historySearch.productInfo as ActivityDetailProductInfo;

        return {
          id: historySearch.id,
          title: activityDetailProductInfo.name,
          line1: historySearch.localizedDateRange,
          line2: activityDetailProductInfo.duration,
          deepLinkUrl: activityDetailProductInfo.searchUrl,
          icon: ItemTypeMap.ACTIVITY_SEARCH.icon,
        };
      case ItemTypeMap.FLIGHT_SEARCH.type:
        const flightSearchProductInfo = historySearch.productInfo as FlightProductInfo;
        const numberOfTravelers = getNumberOfTravelers(
          flightSearchProductInfo.flightPassengers?.adults!,
          flightSearchProductInfo.flightPassengers?.agesOfChildren!
        );
        const flightTemplate = formatFlightTemplate(numberOfTravelers, flightSearchProductInfo.routeType!);

        return {
          id: historySearch.id,
          title: flightSearchProductInfo.localizedFlightName,
          line1: historySearch.localizedDateRange,
          line2: flightTemplate,
          deepLinkUrl: flightSearchProductInfo.searchUrl,
          icon: ItemTypeMap.FLIGHT_SEARCH.icon,
        };
      case ItemTypeMap.FLIGHT_DETAIL.type:
        const flightDetailProductInfo = historySearch.productInfo as FlightDetailProductInfo;
        const numberOfTravelersFlightDetail = getNumberOfTravelers(
          flightDetailProductInfo.flightPassengers?.adults!,
          flightDetailProductInfo.flightPassengers?.agesOfChildren!
        );
        const flightDetailTemplate = formatFlightTemplate(
          numberOfTravelersFlightDetail,
          flightDetailProductInfo.routeType!
        );

        return {
          id: historySearch.id,
          title: flightDetailProductInfo.localizedFlightName,
          line1: historySearch.localizedDateRange,
          line2: flightDetailTemplate,
          deepLinkUrl: flightDetailProductInfo.searchUrl,
          icon: ItemTypeMap.FLIGHT_SEARCH.icon,
        };
      case ItemTypeMap.PACKAGE_SEARCH.type:
        const packageSearchProductInfo = historySearch.productInfo as PackageProductInfo;

        return {
          id: historySearch.id,
          title: packageSearchProductInfo.localizedPackageName,
          line1: historySearch.localizedDateRange,
          line2: packageSearchProductInfo.localizedPackagesTemplate,
          deepLinkUrl: packageSearchProductInfo.searchUrl,
          icon: ItemTypeMap.PACKAGE_SEARCH.icon,
        };
      case ItemTypeMap.PROPERTY_SEARCH.type:
        const propertySearchProductInfo = historySearch.productInfo as PropertyProductInfo;

        return {
          id: historySearch.id,
          title: propertySearchProductInfo.regionDetail?.name,
          line1: historySearch.localizedDateRange,
          line2: propertySearchProductInfo.localizedRoomsTemplate,
          deepLinkUrl: propertySearchProductInfo.searchUrl,
          icon: ItemTypeMap.PROPERTY_SEARCH.icon,
        };
      case ItemTypeMap.PROPERTY_DETAIL.type:
        const propertyDetailProductInfo = historySearch.productInfo as PropertyDetailProductInfo;

        return {
          id: historySearch.id,
          title: propertyDetailProductInfo.summary.name,
          line1: historySearch.localizedDateRange,
          line2: propertyDetailProductInfo.regionDetail?.name,
          deepLinkUrl: propertyDetailProductInfo.searchUrl,
          icon: ItemTypeMap.PROPERTY_SEARCH.icon,
        };
      default:
        return null;
    }
  };

  const getLocalizedRouteType = (routeType: string): string => {
    return routeTypeMap[routeType](formatText);
  };

  const formatFlightTemplate = (numberOfTravelers: number, routeType: string): string => {
    if (numberOfTravelers != null && numberOfTravelers > 0) {
      const flightOneTravelerText = formatText(locKeys.flightOneTravelerText);
      const flightMultiTravelersText = formatText(locKeys.flightMultiTravelersText, numberOfTravelers);
      const travelersToken = numberOfTravelers === 1 ? flightOneTravelerText : flightMultiTravelersText;

      return `${getLocalizedRouteType(routeType)}, ${travelersToken}`;
    }

    return "";
  };

  const getNumberOfTravelers = (adults: number, agesOfChildren: number[]) => adults + agesOfChildren.length;

  const formatDate = (lastViewedDate: LastViewedDate) => {
    const { year, month, day, hour, minute, second } = lastViewedDate;
    const formattedDate = new Date(year, month, day, hour, minute, second);

    return formattedDate;
  };

  if (recentlyViewedData === null || recentlyViewedData.length < minAmountOfCardsToDisplay) {
    return null;
  }

  return (
    <div
      className="RecentlyViewedWrapper uitk-cell all-cell-1-1 all-t-padding-three all-b-padding-six"
      id={flexComponent.id}
    >
      <div className="all-b-margin-four">
        <UitkHeading {...headingConfig}>{formatText(locKeys.keepPlanningText)}</UitkHeading>
      </div>
      <RecentlyViewedCards cardsData={recentlyViewedData} />
    </div>
  );
});

export default RecentlyViewedWrapper;
